import type { ConversationRuntimeSessionMessage } from '@/IsekaiRPG/modules/conversation/conversationTypes';
import type { GameEventEntry } from '@/IsekaiRPG/modules/event/eventTypes';
import type { useStoryHistoryStore } from './historyStores';
import type { StoryHistoryEntry } from './historyTypes';
import {
  StoryHistoryService,
  type StoryHistoryConversationContext,
} from './storyHistoryService';

type StoryHistoryStore = ReturnType<typeof useStoryHistoryStore>;
type OptionalStoryHistoryStore = StoryHistoryStore | null | undefined;

function dedupeEntryIds(entryIds: string[]): string[] {
  return [...new Set(entryIds.map(entryId => entryId.trim()).filter(Boolean))];
}

// ============================================
// 主历史写入入口
// 统一封装主历史的追加、补偿同步、编辑与删除逻辑。
// 这样各分支系统只负责提供领域消息，不再各自直接操作 storyHistory store。
// ============================================

export class StoryHistoryWriteService {
  static appendGameEvent(store: OptionalStoryHistoryStore, event: GameEventEntry): StoryHistoryEntry | null {
    if (!store) return null;
    return store.appendEntry(StoryHistoryService.fromGameEventEntry(event));
  }

  static appendConversationMessage(
    store: OptionalStoryHistoryStore,
    message: ConversationRuntimeSessionMessage,
    context: StoryHistoryConversationContext,
  ): StoryHistoryEntry | null {
    if (!store) return null;
    return store.appendEntry(StoryHistoryService.fromConversationMessage(message, context));
  }

  static syncConversationMessage(
    store: OptionalStoryHistoryStore,
    message: ConversationRuntimeSessionMessage,
    context: StoryHistoryConversationContext,
  ): StoryHistoryEntry | null {
    if (!store) return null;
    return StoryHistoryService.syncConversationMessage(store, message, context);
  }

  static updateEntryContent(store: OptionalStoryHistoryStore, entryId: string, content: string): boolean {
    if (!store) return false;
    return store.updateEntryContent(entryId, content);
  }

  static updateEntryMetadata(
    store: OptionalStoryHistoryStore,
    entryId: string,
    metadata: Record<string, unknown>,
    merge: boolean = true,
  ): boolean {
    if (!store) return false;
    return store.updateEntryMetadata(entryId, metadata, merge);
  }

  static removeEntry(store: OptionalStoryHistoryStore, entryId: string): boolean {
    if (!store) return false;
    return store.removeEntry(entryId);
  }

  static removeEntries(store: OptionalStoryHistoryStore, entryIds: string[]): string[] {
    const uniqueIds = dedupeEntryIds(entryIds);
    if (!store) return uniqueIds;

    for (const entryId of uniqueIds) {
      store.removeEntry(entryId);
    }

    return uniqueIds;
  }
}
